/**
 * Predicados PUROS sobre o objeto `plan` do tenant.
 *
 * Este módulo não importa NADA de propósito: precisa ser consumível tanto por
 * quem tem o `plan` via AuthContext (`usePlanFeature`, `AppLayout`) quanto por
 * quem já o carregou por conta própria (`settings.js` lê `GET /tenants/me` →
 * `formData.plan`). Um único predicado, quatro consumidores — invariante 4
 * (valor canônico: uma função por formato, ninguém reimplementa).
 */

/** Destino único do CTA de upgrade. */
export const PLAN_UPGRADE_URL = "/subscription/plans";

/**
 * O plano inclui agendamento? (ADR-0026)
 *
 * 🔴 FAIL-SAFE `true` — só `false` EXPLÍCITO bloqueia.
 *
 * Ao contrário das demais capacidades (`crmIncluded` e cia., que fecham por
 * ausência), agendamento existe em TODOS os planos menos um: o Plano
 * Atendimento. Campo ausente do payload — cenário real, porque
 * `GET /auth/me` usa `select` explícito —, `null`, ou plano ainda não
 * carregado significam "não sei", e "não sei" aqui tem que liberar.
 *
 * Errar para bloqueado tira a agenda de um cliente pagante em silêncio. Errar
 * para liberado custa, no máximo, um 403 do backend, que a UI trata com CTA de
 * upgrade. Espelha o `resolveSchedulingIncluded` do backend, também fail-safe
 * `true` (ADR-0026 §3).
 *
 * ⚠️ Não "padronize" para `!!plan?.schedulingIncluded`. A assimetria é a regra.
 *
 * @param {{schedulingIncluded?: boolean}|null|undefined} plan
 * @returns {boolean}
 */
export function isSchedulingIncluded(plan) {
  return plan?.schedulingIncluded !== false;
}

/**
 * Janela, em dias, do aviso "seu teste está acabando".
 * Vive aqui (e não inline no AppLayout) porque o teste do banner assevera sobre
 * ela — número mágico duplicado em teste é bug esperando a próxima mudança.
 */
export const TRIAL_ENDING_SOON_DAYS = 3;

/**
 * Classifica o estado da assinatura do tenant em um AVISO de shell — ou em
 * nada. Puro: recebe o tenant e o instante de referência, devolve dado.
 *
 * 🔴 FAIL-SAFE `null` — só estado EXPLICITAMENTE ruim gera aviso.
 *
 * ⚠️ A assimetria é o INVERSO da de `isSchedulingIncluded` acima, e isso é
 * intencional: lá, "não sei" tem que liberar, porque errar para bloqueado tira
 * a agenda de um cliente pagante. Aqui, "não sei" tem que calar, porque errar
 * para "mostra" acusa de inadimplente quem está em dia — e isso é pior que não
 * avisar. Cenários reais de "não sei", todos devolvendo `null`:
 *   • `user` ainda nulo no primeiro render (antes do refresh do AuthProvider);
 *   • `subscriptionStatus` ausente do payload (backend antigo em produção
 *     enquanto o frontend novo já subiu — a janela de deploy);
 *   • `subscriptionStatus` `null`, ou um status que este código não conhece;
 *   • `TRIAL` sem `trialExpiration` legível: sem data não há prazo, e inventar
 *     urgência sem prazo é o mesmo erro.
 *
 * ⚠️ Não "padronize" para um `switch` com `default` que avisa. O default é calar.
 *
 * `PENDING_CANCELLATION` também cala: o acesso segue até o fim do período pago,
 * e a tela de assinatura já mostra a data. Banner ali seria alarme falso.
 *
 * @param {{subscriptionStatus?: string|null, trialExpiration?: string|Date|null}|null|undefined} tenant
 * @param {number} [now] instante de referência em ms (default: `Date.now()`)
 * @returns {{kind: 'TRIAL_ENDING'|'TRIAL_EXPIRED'|'OVERDUE'|'CANCELED', daysLeft: number}|null}
 */
export function resolveSubscriptionNotice(tenant, now = Date.now()) {
  const status = tenant?.subscriptionStatus;

  if (status === "OVERDUE") return { kind: "OVERDUE", daysLeft: 0 };
  if (status === "CANCELED") return { kind: "CANCELED", daysLeft: 0 };
  if (status !== "TRIAL") return null;

  const expiresAt = new Date(tenant?.trialExpiration ?? NaN).getTime();
  if (Number.isNaN(expiresAt)) return null;

  const msLeft = expiresAt - now;
  if (msLeft <= 0) return { kind: "TRIAL_EXPIRED", daysLeft: 0 };

  // `ceil`: faltando 2,2 dias, restam 3 dias de calendário para o dono agir.
  // Arredondar para baixo faria o aviso mentir a favor da urgência.
  const daysLeft = Math.ceil(msLeft / 86400000);
  if (daysLeft > TRIAL_ENDING_SOON_DAYS) return null;

  return { kind: "TRIAL_ENDING", daysLeft };
}

/**
 * Modos de autoridade financeira do tenant (ADR-0034 — `Tenant.billingMode`).
 *
 * Domínio FECHADO: é um enum no Postgres (`BillingMode`), não uma `String`
 * solta como `subscriptionStatus`. Os três valores são exaustivos.
 *
 *   • `ASAAS`    — quem cobra é o gateway. Checkout, fatura, troca e
 *                  cancelamento self-service existem.
 *   • `MANUAL`   — plano concedido e gerenciado pela Nexus (piloto, acordo
 *                  comercial). O Asaas NUNCA é chamado por este tenant.
 *   • `INTERNAL` — conta interna de teste. Não há cobrança associada.
 *
 * Os literais vivem AQUI e só aqui — invariante 4. Nenhuma tela compara a
 * string crua.
 */
export const BILLING_MODE = {
  ASAAS: "ASAAS",
  MANUAL: "MANUAL",
  INTERNAL: "INTERNAL",
};

/**
 * Resolve o modo de cobrança a partir de qualquer envelope que carregue
 * `billingMode`: o `tenant` de `GET /auth/me`, o `tenant` de `GET /tenants/me`,
 * ou a RAIZ de `GET /tenants/me/plan-usage` (onde o campo vem ao lado de
 * `subscriptionStatus`, não dentro de um `tenant`).
 *
 * 🔴 FAIL-SAFE `ASAAS` — só valor EXPLICITAMENTE conhecido muda o modo.
 *
 * A assimetria é a mesma de `isSchedulingIncluded`, e pelo mesmo motivo: "não
 * sei" tem que LIBERAR. `ASAAS` é o `DEFAULT` da coluna (`NOT NULL DEFAULT
 * 'ASAAS'`, ADR-0034 decisão 11) e o estado de todo cadastro real — a migration
 * classificou 100% da base existente assim. Cenários reais de "não sei", todos
 * devolvendo `ASAAS`:
 *   • campo ausente do payload — backend antigo em produção enquanto o
 *     frontend novo já subiu (a janela de deploy; a migration é aditiva e pode
 *     ir antes do código, então esta janela EXISTE de propósito);
 *   • `billingMode` `null`;
 *   • valor que este código não conhece (um `ALTER TYPE … ADD VALUE` futuro);
 *   • tenant ainda não carregado (`null`/`undefined` no primeiro render).
 *
 * Errar para `MANUAL` esconderia os controles de cobrança — fatura, método de
 * pagamento, cancelamento — de um cliente PAGANTE, que ficaria sem caminho
 * para gerenciar a própria assinatura e sem entender por quê. Errar para
 * `ASAAS` mostra, no pior caso, um botão que o backend recusa com **200** e uma
 * mensagem honesta (`managed_manually` / `not_applicable`). Um dos erros é
 * recuperável pelo usuário; o outro não.
 *
 * ⚠️ O modo NUNCA é inferido pela ausência de `asaasSubscriptionId`. Trial
 * legítimo também não tem assinatura — ela só nasce no checkout —, então todo
 * signup cairia na mesma condição de um piloto concedido. Não é uma inferência
 * ruim: é impossível, porque os dois casos são idênticos no dado (ADR-0034,
 * alternativa (a) rejeitada).
 *
 * @param {{billingMode?: string|null}|null|undefined} source tenant ou envelope
 * @returns {"ASAAS"|"MANUAL"|"INTERNAL"}
 */
export function resolveBillingMode(source) {
  const mode = source?.billingMode;
  return Object.prototype.hasOwnProperty.call(BILLING_MODE, mode)
    ? BILLING_MODE[mode]
    : BILLING_MODE.ASAAS;
}

/**
 * O plano deste tenant é concedido e gerenciado pela Nexus? (`MANUAL`)
 * @param {string|null|undefined} mode saída de `resolveBillingMode`
 * @returns {boolean}
 */
export function isBillingManagedByNexus(mode) {
  return mode === BILLING_MODE.MANUAL;
}

/**
 * É uma conta interna de teste? (`INTERNAL`)
 * @param {string|null|undefined} mode saída de `resolveBillingMode`
 * @returns {boolean}
 */
export function isInternalAccount(mode) {
  return mode === BILLING_MODE.INTERNAL;
}

/**
 * Este tenant tem cobrança self-service? (`ASAAS`)
 *
 * É ESTE o predicado que as telas chamam para decidir se mostram método de
 * pagamento, próxima cobrança, faturas, troca de plano e cancelamento. Os dois
 * predicados acima nomeiam QUAL é o caso especial (para escolher a copy do
 * aviso); este responde a única pergunta que a UI de cobrança precisa fazer.
 *
 * Escrito como igualdade positiva a `ASAAS`, e não como `!isBillingManagedByNexus
 * && !isInternalAccount`: se o enum ganhar um quarto valor um dia, a negação o
 * trataria como self-service em silêncio — e a igualdade positiva o trata como
 * caso especial, que é o lado seguro para uma tela financeira. O fail-safe do
 * `resolveBillingMode` continua garantindo que "não sei" chegue aqui como
 * `ASAAS`.
 *
 * @param {string|null|undefined} mode saída de `resolveBillingMode`
 * @returns {boolean}
 */
export function isSelfServiceBilling(mode) {
  return mode === BILLING_MODE.ASAAS;
}

/**
 * Copy canônica do aviso de modo — uma definição, três telas
 * (`SubscriptionManagement`, `PlansGridPage`, `Agenda/settings`).
 *
 * É INFORMATIVO, não um gate de upgrade: sem cadeado, sem CTA de "ver planos".
 * O tenant não está bloqueado nem atrasado — ele só não é cobrado por aqui.
 *
 * `ASAAS` não tem entrada: modo normal não gera aviso.
 *
 * @param {string|null|undefined} mode saída de `resolveBillingMode`
 * @returns {{title: string, text: string}|null}
 */
export function resolveBillingModeNotice(mode) {
  if (isBillingManagedByNexus(mode)) {
    return {
      title: "Plano concedido e gerenciado pela Nexus",
      text: "Não há cobrança automática nesta conta. Para trocar de plano ou alterar qualquer condição, fale com o suporte.",
    };
  }
  if (isInternalAccount(mode)) {
    return {
      title: "Conta interna de teste",
      text: "Não há cobrança associada a esta conta, e operações financeiras não se aplicam a ela.",
    };
  }
  return null;
}
